import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../DashboardLayout';
import {
    MixingAndBakingProvider,
    useMixingAndBakingContext,
    MB_SCREENS
} from './context/MixingAndBakingContext';
import type { MBCycleRecord } from './context/MixingAndBakingContext';
import { mixingAndBakingService } from './services/mixingAndBakingService';
import CompletedCycleCard from './components/CompletedCycleCard';
import ActiveCycleCard from './components/ActiveCycleCard';
import ShiftPopupModal from './components/ShiftPopupModal';
import SummaryReporting from './components/SummaryReporting';
import ChecklistInformation from './components/ChecklistInformation';
import { applyRecipeToCycleRecord } from './utils/recipeUtils';
import { useMsal } from "@azure/msal-react";
import { SP_MSAL_SCOPE } from '../../../constants/sharepoint';
import moment from 'moment';
import FullPageLoader from '../../common/FullPageLoader';
import './styles/mixingAndBaking.css';

const MixingAndBakingMain: React.FC = () => {
    const { state, dispatch } = useMixingAndBakingContext();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { instance, accounts } = useMsal();

    const [shift, setShift] = useState<string>(sessionStorage.getItem("shiftValue") || "Shift 1");
    const [isShiftPopupOpen, setIsShiftPopupOpen] = useState<boolean>(false);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [viewMode, setViewMode] = useState<'main' | 'summary'>('main');
    const [loading, setLoading] = useState<boolean>(true);

    const resumedTourIdRef = useRef<string | null>(null);
    const tourIdParam = searchParams.get('id') || searchParams.get('TourId') || searchParams.get('tourId');

    // Resume or Initialize Tour
    useEffect(() => {
        const initOrResumeTour = async () => {
            setLoading(true);
            try {
                if (tourIdParam && resumedTourIdRef.current !== tourIdParam) {
                    resumedTourIdRef.current = tourIdParam;
                    const cleanTourId = tourIdParam.replace(/[{}]/g, "").trim().toLowerCase();
                    const tours = await mixingAndBakingService.getTourHistory();
                    const tour = (tours || []).find((t: any) => {
                        const id1 = String(t.cr3ea_prod_rajpura_quality_tourid || "").replace(/[{}]/g, "").trim().toLowerCase();
                        const id2 = String(t.cr3ea_prod_qualitytourid || "").replace(/[{}]/g, "").trim().toLowerCase();
                        const id3 = String(t.id || t.localId || "").replace(/[{}]/g, "").trim().toLowerCase();
                        return id1 === cleanTourId || id2 === cleanTourId || id3 === cleanTourId;
                    });

                    const targetTourId = tour?.cr3ea_prod_rajpura_quality_tourid || tour?.cr3ea_prod_qualitytourid || tour?.id || tourIdParam;
                    dispatch({ type: 'SET_TOUR_ID', payload: targetTourId });

                    if (tour?.cr3ea_shift) {
                        const cleanShift = tour.cr3ea_shift.replace("-", " ");
                        setShift(cleanShift);
                        sessionStorage.setItem("shiftValue", cleanShift);
                    }

                    // Restore session data if present on tour
                    if (tour) {
                        const sData = {
                            site: tour.cr3ea_plantid || "Rajpura",
                            line: tour.cr3ea_lineno || "Line 1",
                            shift: (tour.cr3ea_shift || shift).replace("-", " "),
                            qaExecutive: tour.cr3ea_assigned_qa || tour.cr3ea_tourby || "",
                            productionIncharge: tour.cr3ea_shiftexecutiveproduction || "",
                            productName: tour.cr3ea_runningvariety || tour.cr3ea_mixing_baking_productname || "",
                            executiveName: tour.cr3ea_observedby || (accounts && accounts[0]?.name) || "Shift Executive",
                            batchNo: tour.cr3ea_batchno || tour.cr3ea_mixing_baking_batchno || "",
                            cycle: "Cycle-1",
                            typeOfMixer: tour.cr3ea_mixing_baking_typeofmixer || "Vertical",
                            tourStartDate: tour.cr3ea_tourstartdate || tour.createdon || new Date().toISOString()
                        };
                        dispatch({ type: 'SET_SESSION_DATA', payload: sData });

                        if (sData.productName) {
                            const updatedActive = applyRecipeToCycleRecord({
                                ...state.activeCycleRecord,
                                productName: sData.productName,
                                batchNo: sData.batchNo,
                                lineNo: sData.line,
                                shift: sData.shift,
                                observedBy: sData.qaExecutive,
                                productionIncharge: sData.productionIncharge,
                                executiveName: sData.executiveName
                            }, sData.productName);
                            dispatch({ type: 'SET_ACTIVE_CYCLE_RECORD', payload: updatedActive });
                        }
                    }

                    // Load existing cycles
                    const existingCycles = await mixingAndBakingService.getCyclesForTour(targetTourId);
                    if (existingCycles && existingCycles.length > 0) {
                        dispatch({ type: 'SET_COMPLETED_CYCLES', payload: existingCycles });

                        const maxCycleNum = existingCycles.reduce((max, c) => {
                            const num = parseInt(c.cycle.replace(/\D/g, ""), 10) || 0;
                            return num > max ? num : max;
                        }, 0);
                        const nextCycleNumber = maxCycleNum + 1;
                        dispatch({ type: 'SET_CURRENT_CYCLE', payload: `Cycle-${nextCycleNumber}` });

                        // If tour already has cycles, setup is already done
                        dispatch({ type: 'SET_SCREEN', payload: MB_SCREENS.CYCLE_ENTRY });
                    } else if (tour?.cr3ea_runningvariety || tour?.cr3ea_mixing_baking_productname) {
                        // Tour already started with product metadata: switch directly to cycle entry
                        dispatch({ type: 'SET_SCREEN', payload: MB_SCREENS.CYCLE_ENTRY });
                    }
                }
            } catch (err) {
                console.error("Error initializing Mixing & Baking session:", err);
            } finally {
                setLoading(false);
            }
        };

        initOrResumeTour();
    }, [tourIdParam]);

    // Close 3-dots menu on outside click
    useEffect(() => {
        const handleClickOutside = () => setIsMenuOpen(false);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Complete Tour and Save Active Cycle
    const handleCompleteTour = async (record: MBCycleRecord, file: File | null) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            let currentTourId = state.currentTourId;

            // Ensure parent tour exists
            if (!currentTourId) {
                currentTourId = await mixingAndBakingService.createOrGetTour({
                    site: state.sessionData?.site || "Rajpura",
                    line: state.sessionData?.line || "Line 1",
                    shift: state.sessionData?.shift || shift,
                    qaExecutive: record.observedBy || (accounts && accounts[0]?.name) || "QA Executive",
                    productionIncharge: record.productionIncharge || "Production Incharge",
                    productName: record.productName,
                    executiveName: record.executiveName || record.observedBy,
                    batchNo: record.batchNo,
                    cycle: record.cycle,
                    typeOfMixer: record.forming?.typeofMixer || "Vertical",
                    tourStartDate: state.sessionData?.tourStartDate || new Date().toISOString()
                });
                if (currentTourId) {
                    dispatch({ type: 'SET_TOUR_ID', payload: currentTourId });
                }
            }

            // Upload attachment if present
            if (file && currentTourId) {
                try {
                    let spToken = "";
                    if (accounts && accounts.length > 0) {
                        try {
                            const tokenResponse = await instance.acquireTokenSilent({
                                scopes: [SP_MSAL_SCOPE],
                                account: accounts[0]
                            });
                            spToken = tokenResponse.accessToken;
                        } catch (_) {}
                    }
                    const attItem = await mixingAndBakingService.uploadAttachment(currentTourId, record.cycle, file, spToken);
                    if (attItem) {
                        record.attachments = [
                            ...(record.attachments || []),
                            attItem
                        ];
                    }
                } catch (fileErr) {
                    console.warn("Could not upload file to SharePoint:", fileErr);
                }
            }

            // Save Cycle Record to Dataverse + IndexedDB
            await mixingAndBakingService.saveCycleRecord(currentTourId || "", record);

            // Mark Parent Tour Completed
            if (currentTourId) {
                await mixingAndBakingService.updateTourStatus(currentTourId, "Completed");
            }

            alert("Quality Tour completed and locked successfully!");
            navigate("/home");
        } catch (err: any) {
            console.error("Failed to complete tour:", err);
            alert("Tour saved locally with notice: " + (err.message || err));
            navigate("/home");
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const currentDateFormatted = moment().format("DD/MM/YYYY");
    const activeCycleNum = parseInt(state.currentCycle.replace(/\D/g, ""), 10) || (state.completedCycles.length + 1);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Loading Mixing & Baking Process...</p>
            </div>
        );
    }

    if (viewMode === 'summary' || state.currentScreen === MB_SCREENS.SUMMARY) {
        return (
            <div id="rajpuraQualityDashboard">
                <div className="plan-tour-content">
                    <div style={{ marginBottom: '16px' }}>
                        <button
                            type="button"
                            className="bs-btn bs-btn-outline-primary"
                            onClick={() => {
                                setViewMode('main');
                                dispatch({ type: 'SET_SCREEN', payload: MB_SCREENS.CYCLE_ENTRY });
                            }}
                        >
                            ← Back to Cycle Entry
                        </button>
                    </div>
                    <SummaryReporting />
                </div>
            </div>
        );
    }

    return (
        <div id="rajpuraQualityDashboard">
            <FullPageLoader isLoading={state.isLoading || loading} message="Processing Mixing & Baking request... Please wait." />
            <main className="plan-tour-content">
                {/* 1. HEADER SECTION */}
                <div className="plan-tour-header">
                    <div className="tour-header-info-wrapper">
                        <h3 className="tour-header-title">MIXING & BAKING PROCESS (RAJPURA)</h3>
                        <div className="tour-header-meta">
                            <span className="badge" id="shiftBadge">{state.sessionData?.shift || shift}</span>
                            <span className="tour-date" id="currentDay">{currentDateFormatted}</span>
                        </div>
                        <div className="bs-menu-parent">
                            <button
                                type="button"
                                className="bs-btn icon-btn bs-menu-opener"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsMenuOpen(!isMenuOpen);
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <path d="M7.56043 9.00004C7.56043 9.79533 8.20514 10.44 9.00043 10.44C9.79572 10.44 10.4404 9.79533 10.4404 9.00004C10.4404 8.20475 9.79572 7.56004 9.00043 7.56004C8.20514 7.56004 7.56043 8.20475 7.56043 9.00004Z" fill="#0C0D10" />
                                    <path d="M7.56043 13.59C7.56043 14.3853 8.20514 15.03 9.00043 15.03C9.79572 15.03 10.4404 14.3853 10.4404 13.59C10.4404 12.7947 9.79572 12.15 9.00043 12.15C8.20514 12.15 7.56043 12.7947 7.56043 13.59Z" fill="#0C0D10" />
                                    <path d="M7.56043 4.41002C7.56043 5.20531 8.20514 5.85002 9.00043 5.85002C9.79572 5.85002 10.4404 5.20531 10.4404 4.41002C10.4404 3.61473 9.79572 2.97002 9.00043 2.97002C8.20514 2.97002 7.56043 3.61473 7.56043 4.41002Z" fill="#0C0D10" />
                                </svg>
                            </button>
                            <div className={`bs-menu ${isMenuOpen ? 'is-menu-active' : ''}`}>
                                <button
                                    type="button"
                                    className="bs-btn bs-menu-btn shift-popup-opener"
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        setIsShiftPopupOpen(true);
                                    }}
                                >
                                    Change Shift
                                </button>
                                <button
                                    type="button"
                                    className="bs-btn bs-menu-btn"
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        setViewMode('summary');
                                    }}
                                >
                                    Summary & Analytics
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. MAIN WORKFLOW BODY */}
                <div className="plan-tour-body" style={{ padding: '15px' }}>
                    {/* SCREEN 1: CHECKLIST INFORMATION FORM */}
                    {state.currentScreen === MB_SCREENS.CHECKLIST_INFO && (
                        <ChecklistInformation />
                    )}

                    {/* SCREEN 2: CYCLES CONTAINER & SUMMARY HEADER */}
                    {state.currentScreen === MB_SCREENS.CYCLE_ENTRY && (
                        <div id="screen-cycles-container">
                            {/* Persistent Read-Only Checklist Information Header Card */}
                            <div id="mb-info-header-card" className="bs-card mb-4" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '20px' }}>
                                <div className="bs-card-header" style={{ padding: '12px 18px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <h4 className="bs-card-title" style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>Checklist Information</h4>
                                        <span className="badge" id="mb-header-status-badge" style={{ backgroundColor: '#d1fae5', color: '#065f46', fontWeight: 600, fontSize: '11px', padding: '3px 8px', borderRadius: '4px' }}>
                                            Active Session
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        id="mb-edit-info-btn"
                                        className="bs-btn"
                                        style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 600, color: '#475569', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                        onClick={() => dispatch({ type: 'SET_SCREEN', payload: MB_SCREENS.CHECKLIST_INFO })}
                                    >
                                        Edit Info
                                    </button>
                                </div>
                                <div className="tour-cycle-info-wrapper" style={{ padding: '14px 18px' }}>
                                    <div className="tour-cyle-start-info" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
                                        <div className="start-info-item">
                                            <p className="item-label" style={{ fontSize: '11px', color: '#64748b', margin: '0 0 2px 0', textTransform: 'uppercase', fontWeight: 600 }}>Site</p>
                                            <p className="item-value" id="hdr-site" style={{ fontWeight: 600, color: '#0f172a', margin: 0, fontSize: '13px' }}>{state.sessionData?.site || "Rajpura"}</p>
                                        </div>
                                        <div className="start-info-item">
                                            <p className="item-label" style={{ fontSize: '11px', color: '#64748b', margin: '0 0 2px 0', textTransform: 'uppercase', fontWeight: 600 }}>Line No</p>
                                            <p className="item-value" id="hdr-line" style={{ fontWeight: 600, color: '#0f172a', margin: 0, fontSize: '13px' }}>{state.sessionData?.line || "Line 1"}</p>
                                        </div>
                                        <div className="start-info-item" style={{ gridColumn: 'span 2' }}>
                                            <p className="item-label" style={{ fontSize: '11px', color: '#64748b', margin: '0 0 2px 0', textTransform: 'uppercase', fontWeight: 600 }}>Product Name</p>
                                            <p className="item-value" id="hdr-product" style={{ fontWeight: 700, color: '#0284c7', margin: 0, fontSize: '13px' }}>{state.sessionData?.productName || "-"}</p>
                                        </div>
                                        <div className="start-info-item">
                                            <p className="item-label" style={{ fontSize: '11px', color: '#64748b', margin: '0 0 2px 0', textTransform: 'uppercase', fontWeight: 600 }}>QA Executive</p>
                                            <p className="item-value" id="hdr-qa" style={{ fontWeight: 600, color: '#0f172a', margin: 0, fontSize: '13px' }}>{state.sessionData?.qaExecutive || "-"}</p>
                                        </div>
                                        <div className="start-info-item">
                                            <p className="item-label" style={{ fontSize: '11px', color: '#64748b', margin: '0 0 2px 0', textTransform: 'uppercase', fontWeight: 600 }}>Production Executive</p>
                                            <p className="item-value" id="hdr-prod" style={{ fontWeight: 600, color: '#0f172a', margin: 0, fontSize: '13px' }}>{state.sessionData?.productionIncharge || "-"}</p>
                                        </div>
                                        <div className="start-info-item">
                                            <p className="item-label" style={{ fontSize: '11px', color: '#64748b', margin: '0 0 2px 0', textTransform: 'uppercase', fontWeight: 600 }}>Shift Executive</p>
                                            <p className="item-value" id="hdr-shift-exec" style={{ fontWeight: 600, color: '#0f172a', margin: 0, fontSize: '13px' }}>{state.sessionData?.executiveName || "-"}</p>
                                        </div>
                                        <div className="start-info-item">
                                            <p className="item-label" style={{ fontSize: '11px', color: '#64748b', margin: '0 0 2px 0', textTransform: 'uppercase', fontWeight: 600 }}>Batch No</p>
                                            <p className="item-value" id="hdr-batch" style={{ fontWeight: 600, color: '#0f172a', margin: 0, fontSize: '13px' }}>{state.sessionData?.batchNo || "-"}</p>
                                        </div>
                                        <div className="start-info-item">
                                            <p className="item-label" style={{ fontSize: '11px', color: '#64748b', margin: '0 0 2px 0', textTransform: 'uppercase', fontWeight: 600 }}>Shift</p>
                                            <p className="item-value" id="hdr-shift" style={{ fontWeight: 600, color: '#0f172a', margin: 0, fontSize: '13px' }}>{state.sessionData?.shift || shift}</p>
                                        </div>
                                        <div className="start-info-item">
                                            <p className="item-label" style={{ fontSize: '11px', color: '#64748b', margin: '0 0 2px 0', textTransform: 'uppercase', fontWeight: 600 }}>Date</p>
                                            <p className="item-value" id="hdr-date" style={{ fontWeight: 600, color: '#0f172a', margin: 0, fontSize: '13px' }}>{currentDateFormatted}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Dynamic List of Cycles */}
                            <div className="tour-cycle-card-panel-lists">
                                {/* Completed Cycles */}
                                {state.completedCycles.map((cycleItem, index) => {
                                    const cycleIndex = parseInt(cycleItem.cycle.replace(/\D/g, ""), 10) || (index + 1);
                                    return (
                                        <CompletedCycleCard
                                            key={cycleItem.id || `completed-cycle-${cycleIndex}`}
                                            cycleNum={cycleIndex}
                                            cycleData={cycleItem}
                                            tourDate={state.sessionData?.tourStartDate}
                                        />
                                    );
                                })}

                                {/* Active In-Progress Cycle */}
                                <ActiveCycleCard
                                    key={`active-cycle-${activeCycleNum}`}
                                    cycleNum={activeCycleNum}
                                    initialRecord={state.activeCycleRecord}
                                    shift={state.sessionData?.shift || shift}
                                    line={state.sessionData?.line || "Line 1"}
                                    qaExecutive={state.sessionData?.qaExecutive || ""}
                                    productionIncharge={state.sessionData?.productionIncharge || ""}
                                    productName={state.sessionData?.productName || ""}
                                    batchNo={state.sessionData?.batchNo || ""}
                                    onCancel={() => navigate('/home')}
                                    onCompleteTour={handleCompleteTour}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* Shift Selection Popup */}
            <ShiftPopupModal
                isOpen={isShiftPopupOpen}
                currentShift={shift}
                onClose={() => setIsShiftPopupOpen(false)}
                onShiftChange={(newShift) => {
                    setShift(newShift);
                    sessionStorage.setItem("shiftValue", newShift);
                    if (state.sessionData) {
                        dispatch({
                            type: 'SET_SESSION_DATA',
                            payload: {
                                ...state.sessionData,
                                shift: newShift
                            }
                        });
                    }
                }}
            />
        </div>
    );
};

const MixingAndBaking: React.FC = () => {
    return (
        <DashboardLayout>
            <MixingAndBakingProvider>
                <MixingAndBakingMain />
            </MixingAndBakingProvider>
        </DashboardLayout>
    );
};

export default MixingAndBaking;
